import {
  AnimationName,
  Button,
  ButtonProps,
  IconName,
  LottieAnimation,
  SafeHtml,
  Text,
  TextColorVariant,
} from '@billy/design-system'
import type { TOptions } from 'i18next'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TrackingContext } from '../../enums/trackingContext'
import { ButtonPremium } from '../PremiumComponents'
import { ButtonProtected, ProtectableProps } from '../ProtectedComponents'
import * as Styled from './styles'
import { AnimationDimensions } from './types/emptyViewType'

interface EmptyTypeProps extends Partial<ProtectableProps> {
  animationName?: AnimationName
  animationDimensions?: AnimationDimensions
  buttonIcon?: IconName
  buttonSize?: ButtonProps['size']
  descriptionColor?: TextColorVariant
  descriptionOptions?: TOptions
  illustration?: ReactElement
  isLockedByUpsell?: boolean
  onClick?: () => void
  showCTA?: boolean
  tButton?: string
  tDescription: string
  trackingContext?: TrackingContext
  tTitle: string
}

export const EmptyView = ({
  animationName,
  animationDimensions = {
    height: 150,
    width: 150,
  },
  buttonSize = 'lg',
  buttonIcon = 'circlePlus',
  descriptionColor = 'secondary',
  descriptionOptions,
  illustration,
  isLockedByUpsell,
  onClick,
  scopes,
  showCTA,
  tButton,
  tDescription,
  tTitle,
  trackingContext,
}: EmptyTypeProps): ReactElement => {
  const { t } = useTranslation()

  const ctaButtonProps: ButtonProps = useMemo(
    () =>
      ({
        onClick,
        icon: buttonIcon,
        size: buttonSize,
      }) as ButtonProps,
    [buttonIcon, buttonSize, onClick],
  )

  if (scopes && !trackingContext) {
    throw new Error('You have to define "trackingContext" when button is protected with scopes!')
  }

  const shouldUseProtectedButton = scopes && trackingContext
  const shouldUsePremiumButton = !scopes && trackingContext
  const shouldUseRegularButton = !shouldUseProtectedButton && !shouldUsePremiumButton

  return (
    <Styled.EmptyViewWrapper>
      {animationName && (
        <LottieAnimation
          height={animationDimensions.height}
          width={animationDimensions.width}
          isClickToPauseDisabled
          name={animationName}
        />
      )}

      {illustration && <div>{illustration}</div>}

      <Styled.TitleWrapper>
        <Text alignment="center" variant="h2">
          <SafeHtml as="span" htmlContent={t(tTitle)} />
        </Text>
      </Styled.TitleWrapper>

      <Styled.TextWrapper>
        <Text alignment="center" colorVariant={descriptionColor} variant="bodyLG">
          <SafeHtml as="span" htmlContent={t(tDescription, descriptionOptions)} />
        </Text>
      </Styled.TextWrapper>

      {showCTA && (
        <Styled.ButtonWrapper>
          {shouldUseProtectedButton && (
            <ButtonProtected {...ctaButtonProps} scopes={scopes} trackingContext={trackingContext as TrackingContext}>
              {tButton && t(tButton)}
            </ButtonProtected>
          )}
          {shouldUsePremiumButton && (
            <ButtonPremium
              {...ctaButtonProps}
              isBlocked={isLockedByUpsell}
              trackingContext={trackingContext as TrackingContext}
            >
              {tButton && t(tButton)}
            </ButtonPremium>
          )}
          {shouldUseRegularButton && <Button {...ctaButtonProps}>{tButton && t(tButton)}</Button>}
        </Styled.ButtonWrapper>
      )}
    </Styled.EmptyViewWrapper>
  )
}
